import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Input,
  Label,
  Button,
  Collapse
} from "reactstrap";
import { listAllPausedSubscription } from "../Subscription/_requests";
import { listSubscriptionPlansListActive } from "./_requests";
import { listProduct } from "../Products/_requests";
import TableContainer from "../../Components/Common/TableContainer";
import { formatDate } from "../../helpers/FunctionHelpers";

interface UserPauseSubscriptionListProps {
  refresh: boolean;
  userId?: string;
}

const UserPauseSubscriptionList = ({ refresh, userId }: UserPauseSubscriptionListProps) => {
  const { id } = useParams<{ id: string }>();
  const [pauses, setPauses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  interface LookupItem {
    id: string | number;
    name: string;
    [key: string]: any;
  }

  const [plansList, setPlansList] = useState<LookupItem[]>([]);
  const [productsList, setProductsList] = useState<LookupItem[]>([]);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    subscription_id: "",
    product_id: ""
  });

  const fetchDataLists = async () => {
    try {
      const [plansRes, productsRes]: any = await Promise.all([
        listSubscriptionPlansListActive({}),
        listProduct()
      ]);

      if (plansRes && plansRes.data) {
        // Flatten grouped plans (Days, Month, Year groups)
        const flatPlans: LookupItem[] = Array.isArray(plansRes.data)
          ? plansRes.data.flatMap((group: any) => group.products || [])
          : [];

        // Remove duplicates if any
        const uniquePlans = Array.from(new Map(flatPlans.map((item: LookupItem) => [item.id, item])).values());
        setPlansList(uniquePlans);
      }

      if (productsRes && productsRes.data) {
        setProductsList(productsRes.data || []);
      }
    } catch (error) {
      console.error("Error fetching filter lists:", error);
    }
  };

  const fetchPausedSubscriptions = async () => {
    try {
      setLoading(true);
      const currentUserId = userId || id;

      const params: any = {
        user_id: currentUserId,
        ...filters
      };

      const response: any = await listAllPausedSubscription(params);
      if (response && response.data) {
        setPauses(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching paused subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataLists();
  }, []);

  useEffect(() => {
    fetchPausedSubscriptions();
  }, [id, refresh, userId, filters]);

  const filteredAndGroupedPauses = useMemo(() => {
    let filtered = [...pauses];

    // Apply frontend filtering as a fallback/redundancy
    if (filters.fromDate || filters.toDate) {
      filtered = filtered.filter((pause: any) => {
        if (!pause.date) return false;
        const pauseDate = moment(pause.date).startOf('day');
        const from = filters.fromDate ? moment(filters.fromDate).startOf('day') : null;
        const to = filters.toDate ? moment(filters.toDate).startOf('day') : null;

        if (from && pauseDate.isBefore(from)) return false;
        if (to && pauseDate.isAfter(to)) return false;
        return true;
      });
    }

    const grouped: any = {};

    filtered.forEach((pause: any) => {
      const subId = pause.user_subscription_id || pause.userSubscription?.id;
      if (!subId) return;

      if (!grouped[subId]) {
        grouped[subId] = {
          ...pause,
          dates: [pause.date]
        };
      } else {
        // Only add date if it's not already there
        if (!grouped[subId].dates.includes(pause.date)) {
          grouped[subId].dates.push(pause.date);
        }
      }
    });

    return Object.values(grouped).map((item: any) => ({
      ...item,
      dates: item.dates.sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())
    }));
  }, [pauses, filters.fromDate, filters.toDate]);

  const columns = useMemo(
    () => [
      {
        header: "S.No",
        accessorKey: "id",
        cell: (cell: any) => {
          const { pageIndex, pageSize } = cell.table.getState().pagination;
          return pageIndex * pageSize + cell.row.index + 1;
        },
      },
      {
        header: "Subscription Details",
        accessorKey: "userSubscription.subscription.name",
        cell: (cell: any) => (
          <div className="d-flex flex-column gap-2" style={{ minWidth: '220px' }}>
            <div className="p-2 bg-primary-subtle rounded-3 border border-primary border-opacity-10">
              <div className="fw-bold text-primary fs-13">{cell.row.original.userSubscription?.subscription?.name || "-"}</div>
              <div className="text-muted fs-11">#ID: {cell.row.original.userSubscription?.id}</div>
            </div>
            <div className="mt-2 pt-1 border-top border-light d-flex flex-wrap gap-2 fs-10">
              <span className="text-muted"><span className="fw-bold">Start:</span> {formatDate(cell.row.original.userSubscription?.start_date)}</span>
              <span className="text-muted"><span className="fw-bold">End:</span> {cell.row.original.userSubscription?.end_date ? formatDate(cell.row.original.userSubscription?.end_date) : "N/A"}</span>
            </div>
            <div className="d-flex flex-column gap-1">
              {cell.row.original.userSubscription?.details?.map((detail: any, idx: number) => (
                <div key={idx} className="p-2 border rounded-3 bg-light bg-opacity-50">
                  <div className="fw-semibold text-dark fs-12">{detail.product?.name}</div>
                  <div className="text-muted fs-10 mt-1">
                    <i className="ri-shopping-basket-2-line me-1"></i>
                    {detail.variant_value} {detail.variant?.name}
                  </div>
                </div>
              )) || "-"}
            </div>
          </div>
        ),
      },
      {
        header: "Paused Subscription Calendar",
        accessorKey: "dates",
        cell: (cell: any) => {
          const dates = cell.row.original.dates;

          // Group raw dates by Month
          const datesByMonth: any = {};
          dates.forEach((d: string) => {
            const key = moment(d).format("MMMM YYYY");
            if (!datesByMonth[key]) datesByMonth[key] = [];
            datesByMonth[key].push(d);
          });

          return (
            <div className="py-2" style={{ minWidth: '500px' }}>
              <div className="d-flex align-items-center p-2 mb-3 bg-danger-subtle text-danger rounded-3 border border-danger border-opacity-10 fw-bold fs-12">
                <i className="ri-calendar-event-line me-2"></i>
                Total Paused: {dates.length} Days
              </div>

              <div className="d-flex flex-wrap gap-3">
                {Object.keys(datesByMonth).map((monthKey, mIdx) => {
                  const pausedDates = datesByMonth[monthKey];
                  const monthMoment = moment(monthKey, "MMMM YYYY");
                  const startDay = monthMoment.startOf('month').day();
                  const daysInMonth = monthMoment.daysInMonth();
                  
                  const days = [];
                  for (let i = 0; i < startDay; i++) days.push(null);
                  for (let i = 1; i <= daysInMonth; i++) days.push(i);

                  return (
                    <div key={mIdx} className="mini-calendar border rounded-3 p-2 bg-white shadow-sm" style={{ width: '165px' }}>
                      <div className="text-center fw-bold text-dark mb-2 fs-11 border-bottom pb-1">{monthKey}</div>
                      <div className="d-grid gap-1" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
                          <div key={d} className="text-muted text-center fw-bold" style={{ fontSize: '8px' }}>{d}</div>
                        ))}
                        {days.map((day, idx) => {
                          if (day === null) return <div key={idx} />;
                          const isPaused = pausedDates.some((pd: string) => moment(pd).date() === day);
                          return (
                            <div 
                              key={idx} 
                              className={`text-center rounded-circle d-flex align-items-center justify-content-center ${isPaused ? 'bg-danger text-white fw-bold shadow-sm' : 'text-muted'}`}
                              style={{ 
                                width: '18px', 
                                height: '18px', 
                                fontSize: '9px',
                                border: isPaused ? 'none' : '1px solid #f8f9fa',
                                cursor: 'default'
                              }}
                              title={isPaused ? `Paused on ${day} ${monthKey}` : ''}
                            >
                              {day}
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-center mt-2 pt-1 border-top border-light smaller text-danger fw-bold" style={{ fontSize: '9px' }}>
                        {pausedDates.length} Days Paused
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        },
      },
      {
        header: "Status",
        cell: () => (
          <Badge color="warning" className="text-uppercase" pill>
            Paused
          </Badge>
        ),
      },
    ],
    []
  );

  return (
    <div className="">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Paused Subscription Details</h5>
            <div className="d-flex gap-2">
              <Button
                color="soft-primary"
                size="sm"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`btn-icon waves-effect waves-light d-flex align-items-center gap-1 ${isFilterOpen ? 'active' : ''}`}
                style={{ width: 'auto', padding: '0 10px' }}
              >
                <i className="ri-filter-2-line align-middle"></i>
                <span>Filters</span>
              </Button>
            </div>
          </CardHeader>

          <Collapse isOpen={isFilterOpen}>
            <CardBody className="border-bottom bg-light bg-opacity-50">
              <Row className="g-3">
                <Col md={3}>
                  <Label className="small fw-bold text-muted mb-1">From Date</Label>
                  <Input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                    className="form-control-sm"
                  />
                </Col>
                <Col md={3}>
                  <Label className="small fw-bold text-muted mb-1">To Date</Label>
                  <Input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                    className="form-control-sm"
                  />
                </Col>
                <Col md={3}>
                  <Label className="small fw-bold text-muted mb-1">Subscription Plan</Label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.subscription_id}
                    onChange={(e) => setFilters({ ...filters, subscription_id: e.target.value })}
                  >
                    <option value="">All Plans</option>
                    {plansList.map((plan: LookupItem) => (
                      <option key={plan.id} value={plan.id}>{plan.name}</option>
                    ))}
                  </select>
                </Col>
                <Col md={3}>
                  <Label className="small fw-bold text-muted mb-1">Product</Label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.product_id}
                    onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
                  >
                    <option value="">All Products</option>
                    {productsList.map((prod: LookupItem) => (
                      <option key={prod.id} value={prod.id}>{prod.name}</option>
                    ))}
                  </select>
                </Col>
                <Col md={12} className="d-flex justify-content-end gap-2 mt-2">
                  <Button
                    color="soft-danger"
                    size="sm"
                    className="px-4"
                    onClick={() => setFilters({ fromDate: "", toDate: "", subscription_id: "", product_id: "" })}
                    disabled={!filters.fromDate && !filters.toDate && !filters.subscription_id && !filters.product_id}
                  >
                    <i className="ri-refresh-line align-middle me-1"></i>
                    Reset All Filters
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Collapse>

          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading paused subscriptions...</p>
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredAndGroupedPauses}
                isGlobalFilter={true}
                customPageSize={10}
                divClass="table-responsive mb-1"
                tableClass="mb-0 table-bordered border-light align-top"
                theadClass="table-light text-uppercase fw-bold text-muted small"
                SearchPlaceholder="Search paused subscriptions..."
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default UserPauseSubscriptionList;
